import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DatabaseBackup, Download, Upload, AlertTriangle, Loader2, CheckCircle2 } from "lucide-react";
import { Panel } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, downloadBlob } from "@/lib/api";

const LABELS = { clients: "clienti", users: "utenti", subscriptions: "abbonamenti", documents: "documenti", pages: "pagine" };

export default function BackupPanel() {
  const [info, setInfo] = useState(null);
  const [file, setFile] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/admin/backup/info").then((r) => setInfo(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const download = async () => {
    setBusy(true);
    try {
      await downloadBlob("/admin/backup", `backup_bacheca_marigot_${new Date().toISOString().slice(0, 10)}.json`);
      toast.success("Backup scaricato");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("confirm", confirm);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/restore", fd);
      toast.success(`Ripristino completato: ${Object.entries(data.counts).map(([k, v]) => `${v} ${LABELS[k]}`).join(", ")}`);
      setFile(null); setConfirm(""); setOpen(false);
      load();
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="Backup e ripristino dati" testid="backup-panel"
      action={<Button onClick={download} disabled={busy} className="bg-blue-600 hover:bg-blue-500" data-testid="backup-download-button">{busy && !open ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Download size={15} className="mr-1.5" />}Scarica backup</Button>}>
      <div className="px-5 py-4 space-y-4 text-sm">
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-slate-400" data-testid="backup-counts">
          {info ? Object.entries(info.counts).map(([k, v]) => <span key={k}><strong className="text-slate-100 font-mono">{v}</strong> {LABELS[k]}</span>) : "…"}
        </div>
        <p className="text-xs text-slate-500 leading-relaxed">
          Il backup è un file JSON con clienti, utenti di accesso, abbonamenti, pagine e l'elenco documenti. I file dei documenti restano nello storage e vengono ricollegati al ripristino.
          Conservalo in un posto sicuro: contiene dati riservati.
        </p>
        {info?.last_restore && <p className="text-xs text-slate-500">Ultimo ripristino: {new Date(info.last_restore.at).toLocaleString("it-IT")} da {info.last_restore.by}</p>}

        {!open ? (
          <Button variant="outline" onClick={() => setOpen(true)} className="border-slate-700 bg-transparent" data-testid="backup-restore-toggle"><Upload size={15} className="mr-1.5" />Ripristina da un backup…</Button>
        ) : (
          <div className="rounded-md border border-rose-500/40 bg-rose-500/5 p-4 space-y-3" data-testid="restore-box">
            <div className="flex items-start gap-2 text-rose-300"><AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span>Il ripristino <strong>sostituisce tutti i dati attuali</strong> con quelli del file. Prima viene salvata una copia di sicurezza dello stato attuale. Il tuo account amministratore resta sempre attivo.</span>
            </div>
            <label className="flex items-center gap-3 rounded-md border border-dashed border-slate-700 bg-slate-900/40 px-4 py-3 cursor-pointer hover:border-rose-400/50 transition-colors">
              <DatabaseBackup size={18} className="text-rose-300 shrink-0" />
              <span className="truncate text-slate-300" data-testid="restore-file-name">{file ? file.name : "Scegli il file backup_bacheca_marigot_….json"}</span>
              <input type="file" accept=".json,application/json" className="hidden" onChange={(e) => setFile(e.target.files[0] || null)} data-testid="restore-file-input" />
            </label>
            <div className="flex flex-wrap items-center gap-3">
              <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder='Scrivi RIPRISTINA per confermare' className="max-w-xs font-mono" data-testid="restore-confirm-input" />
              <Button onClick={restore} disabled={!file || confirm.trim().toUpperCase() !== "RIPRISTINA" || busy} className="bg-rose-600 hover:bg-rose-500" data-testid="restore-submit-button">
                {busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <CheckCircle2 size={15} className="mr-1.5" />}Ripristina ora
              </Button>
              <Button variant="ghost" onClick={() => { setOpen(false); setFile(null); setConfirm(""); }} data-testid="restore-cancel-button">Annulla</Button>
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
}
